import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function CancellationMonitor() {
  const [cancellations, setCancellations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fillingId, setFillingId] = useState(null);

  useEffect(() => {
    loadCancellations();
  }, []);

  useEffect(() => {
  // If no pending fill events, don't poll
  const hasPending = cancellations.some(
    (c) => c.fill_status === 'pending'
  );
  if (!hasPending) return;

  // Poll every 10 seconds while at least one slot is filling
  const id = setInterval(() => {
    loadCancellations();
  }, 10000);

  // Cleanup when:
  // - list changes
  // - component unmounts
  return () => clearInterval(id);
}, [cancellations]);


  const loadCancellations = async (showLoader = true) => {
  if (showLoader) setLoading(true);
  try {
    await api.post('/calendar/sync');
    const response = await api.get('/calendar/cancellations');
    const data = (response.data || []).map((c) => ({
      ...c,
      fill_status: c.fill_status || 'none',
    }));
    setCancellations(data);
  } catch (error) {
    console.error(error);
    toast.error('Failed to load cancellations');
  } finally {
    if (showLoader) setLoading(false);
  }
};


  const handleFillSlot = async (appointmentId) => {
    try {
      setFillingId(appointmentId);
      const response = await api.post('/fill-events/start', {
        appointment_id: appointmentId,
      });

      const fillEvent = response.data?.fill_event;
      const customersNotified =
        response.data?.customers_notified ??
        fillEvent?.notified_customers?.length ??
        0;

      toast.success(
        customersNotified > 0
          ? `Sent to ${customersNotified} customer${
              customersNotified === 1 ? '' : 's'
            } – waiting for replies.`
          : 'Fill event started – waiting for replies.'
      );

      // Mark this cancellation as "pending" locally
      setCancellations((prev) =>
        prev.map((c) =>
          c.id === appointmentId ? { ...c, fill_status: 'pending' } : c
        )
      );
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.detail || 'Failed to start fill event'
      );
    } finally {
      setFillingId(null);
    }
  };

  return (
    <Layout active="cancellations">
      <div data-testid="cancellations-page" className="space-y-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1
              className="text-4xl font-bold text-gray-900"
              style={{ fontFamily: 'Manrope' }}
            >
              Cancellation Monitor
            </h1>
            <p className="text-gray-600 mt-2">
              Live feed of detected cancellations & fill attempts
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={loadCancellations}
              disabled={loading}
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700"></div>
          </div>
        ) : cancellations.length === 0 ? (
          <Card className="bg-white border-border shadow-sm">
            <CardContent className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-2">No cancellations detected</p>
              <p className="text-sm text-gray-400">
                Connect your Google Calendar and add staff to start monitoring.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {cancellations.map((cancellation) => {
              const status = cancellation.fill_status || 'none';

              return (
                <Card
                  key={cancellation.id}
                  className="bg-white border-border shadow-sm"
                  data-testid={`cancellation-card-${cancellation.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start gap-4">
                      {/* Left: details */}
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold">
                            {cancellation.service || 'Appointment'}
                          </h3>

                          {status === 'filled' ? (
                            <Badge className="bg-green-100 text-green-700 border-none">
                              Filled
                            </Badge>
                          ) : status === 'pending' ? (
                            <Badge className="bg-amber-100 text-amber-700 border-none">
                              Waiting for confirmation reply…
                            </Badge>
                          ) : (
                            <Badge variant="destructive">Cancelled</Badge>
                          )}
                        </div>

                        <div className="text-sm text-gray-600 space-y-1">
                          <p>
                            <span className="font-medium">Time:</span>{' '}
                            {cancellation.start_time
                              ? new Date(
                                  cancellation.start_time
                                ).toLocaleString()
                              : 'Unknown'}
                          </p>
                          <p>
                            <span className="font-medium">Duration:</span>{' '}
                            {cancellation.duration} minutes
                          </p>
                          {typeof cancellation.price_estimate === 'number' && (
                            <p>
                              <span className="font-medium">Value:</span> $
                              {cancellation.price_estimate.toFixed(2)}
                            </p>
                          )}
                          {status === 'filled' &&
                            typeof cancellation.recovered_revenue ===
                              'number' && (
                              <p className="text-green-700 font-medium">
                                Recovered: $
                                {cancellation.recovered_revenue.toFixed(2)}
                              </p>
                            )}
                        </div>
                      </div>

                      {/* Right: actions / status */}
                      <div className="flex flex-col items-end gap-2">
                        {status === 'none' && (
                          <Button
                            size="sm"
                            className="bg-teal-700 hover:bg-teal-800"
                            onClick={() => handleFillSlot(cancellation.id)}
                            disabled={fillingId === cancellation.id}
                          >
                            {fillingId === cancellation.id
                              ? 'Starting…'
                              : 'Fill slot now'}
                          </Button>
                        )}

                        {status === 'pending' && (
                          <span className="text-xs text-gray-500">
                            Notifying waitlist customers…
                          </span>
                        )}

                        {status === 'filled' && (
                          <span className="text-xs text-gray-500">
                            Slot filled via waitlist.
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
